/**
 * ChangePasswordPage — same split-canvas frame as LoginPage (shares
 * VideoBrandPanel), same §2.16 well kit (fields step DOWN to
 * --surface-inset, no border at rest, 1px lime focus ring) and the same
 * single lime pill CTA (§2.8 — --btn-primary-bg fill, near-black
 * --btn-primary-fg text, no raw hex).
 *
 * Two modes:
 *  - FORCE RESET (mustChangePassword=true): banner "Set a new password to
 *    continue" + no current-password field escape; cannot navigate away.
 *  - VOLUNTARY: standard current + new + confirm flow from settings.
 *
 * On success: clears mustChangePassword flag in the auth store, navigates to /.
 */
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, ShieldAlert, Lock } from 'lucide-react';
import { useChangePassword } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { VideoBrandPanel, BrandMark } from '@/components/auth/AuthBrandPanel';

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------
const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/[0-9]/, 'Must contain a number'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

// ---------------------------------------------------------------------------
// §2.16 well-kit constants (shared with LoginPage by spec, not by import —
// batch file-sets are disjoint).
// Focus ring reuses the shared --ring token (tokens.css; QA R2 fixed its
// alpha to 0.85 so it clears 3:1 non-text contrast in BOTH themes — a local
// fixed-alpha color-mix here would drift back to the theme-blind failure).
// ---------------------------------------------------------------------------
const FOCUS_RING = 'var(--ring)';
const ERROR_RING = '0 0 0 1px var(--error)';
const CTA_HOVER_BG = 'color-mix(in srgb, var(--btn-primary-bg) 90%, var(--seedz-ink))';

// ---------------------------------------------------------------------------
// PasswordField — reusable show/hide input (well anatomy per §2.16)
// ---------------------------------------------------------------------------
interface PasswordFieldProps {
  id: string;
  label: string;
  registration: ReturnType<typeof useForm<ChangePasswordFormValues>>['register'];
  fieldName: keyof ChangePasswordFormValues;
  error?: string;
  autoComplete?: string;
}

function PasswordField({
  id,
  label,
  registration,
  fieldName,
  error,
  autoComplete = 'new-password',
}: PasswordFieldProps) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 500,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 13,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-subtle)',
            pointerEvents: 'none',
            display: 'flex',
          }}
        >
          <Lock width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
        </span>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          {...registration(fieldName)}
          style={{
            width: '100%',
            height: 40,
            padding: '0 40px 0 38px',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text)',
            background: 'var(--surface-inset)',
            border: 'none',
            // §1.3 --radius-inset: input wells sit at 12px, not the app-global 14px.
            borderRadius: 'var(--radius-inset)',
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: error ? ERROR_RING : 'none',
            transition: 'box-shadow var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)',
          }}
          onFocus={(e) => {
            e.currentTarget.style.boxShadow = FOCUS_RING;
          }}
          onBlur={(e) => {
            e.currentTarget.style.boxShadow = error ? ERROR_RING : 'none';
          }}
        />
        {/* §4.2 S2 (GhostIconButton precedent): 44×44 hitbox via transparent
            padding; the visual stays the Ø28 ghost circle (inner span).
            right:-2 keeps the circle centre 20px from the well edge — same
            optics as the old right:6 / Ø28. */}
        <button
          type="button"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            right: -2,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            padding: 0,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-subtle)',
            transition: 'color var(--dur-fast) var(--ease-out)',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLButtonElement;
            el.style.color = 'var(--text)';
            (el.firstElementChild as HTMLElement).style.background = 'var(--surface-2)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLButtonElement;
            el.style.color = 'var(--text-subtle)';
            (el.firstElementChild as HTMLElement).style.background = 'transparent';
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-pill)',
              background: 'transparent',
              transition: 'background var(--dur-fast) var(--ease-out)',
            }}
          >
            {show ? (
              <EyeOff width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
            ) : (
              <Eye width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
            )}
          </span>
        </button>
      </div>
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            color: 'var(--error)',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChangePasswordPage
// ---------------------------------------------------------------------------
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { mustChangePassword, email, setLogin } = useAuthStore();
  const changePasswordMutation = useChangePassword();
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(false);

  const currentId = useId();
  const newId = useId();
  const confirmId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
  });

  const isPending = isSubmitting || changePasswordMutation.isPending;

  async function onSubmit(values: ChangePasswordFormValues) {
    setServerError(null);
    try {
      const session = await changePasswordMutation.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      // Store the new full-privilege session before navigating so the
      // dashboard's first API calls use the fresh token, not the old
      // force-reset token that has no business-endpoint access.
      setLogin(session.accessToken, session.user.role, session.user.email, false, session.user.id);
      setSuccessMessage(true);
      setTimeout(() => navigate('/', { replace: true }), 900);
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      setServerError(e?.message ?? 'An unexpected error occurred. Please try again.');
    }
  }

  return (
    <div
      className="auth-screen"
      style={{
        display: 'flex',
        minHeight: '100vh',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* LEFT PANEL — shared video brand canvas */}
      <VideoBrandPanel />

      {/* ------------------------------------------------------------------ */}
      {/* RIGHT PANEL — change-password form                                   */}
      {/* ------------------------------------------------------------------ */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 32px',
          background: 'var(--surface)',
          minWidth: 0,
        }}
      >
        <div style={{ width: '100%', maxWidth: 360 }}>
          {/* Mobile lockup (hidden at 880px+, the VideoBrandPanel's own
              breakpoint) — carries the accessible brand name */}
          <div className="min-[880px]:hidden" style={{ marginBottom: 32 }}>
            <BrandMark glyphSize={30} label="Seedz Manage" />
          </div>

          {/* Force-reset banner — info accent (not lime: lime is reserved for
              logo/CTA/selection), 3px left accent + ~12% same-hue tint */}
          {mustChangePassword && (
            <div
              role="status"
              className="reveal-1"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '11px 14px',
                marginBottom: 24,
                borderRadius: 'var(--radius-md)',
                borderLeft: '3px solid var(--accent)',
                background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
              }}
            >
              <ShieldAlert
                width={16}
                height={16}
                strokeWidth={1.5}
                style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }}
                aria-hidden="true"
              />
              <div>
                <p
                  style={{
                    margin: '0 0 2px',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  Set a new password to continue
                </p>
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {email
                    ? `Your account (${email}) requires a password change before you can proceed.`
                    : 'Your account requires a password change before you can proceed.'}
                </p>
              </div>
            </div>
          )}

          {/* Heading */}
          <div style={{ marginBottom: 28 }} className="reveal-1">
            <h2
              style={{
                margin: '0 0 6px',
                fontFamily: 'var(--font-display)',
                fontSize: '1.75rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--text)',
              }}
            >
              {mustChangePassword ? 'Set new password' : 'Change password'}
            </h2>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Choose a strong password with at least 8 characters, one uppercase letter, and one
              number.
            </p>
          </div>

          {/* Success state — positive-status tint (health-green is the
              sanctioned positive hue) */}
          {successMessage && (
            <div
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                marginBottom: 20,
                borderRadius: 'var(--radius-md)',
                background: 'var(--health-green-bg)',
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ color: 'var(--health-green)', flexShrink: 0 }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  color: 'var(--health-green)',
                }}
              >
                Password updated. Redirecting…
              </p>
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
          >
            <div className="reveal-2">
              <PasswordField
                id={currentId}
                label="Current password"
                registration={register}
                fieldName="currentPassword"
                error={errors.currentPassword?.message}
                autoComplete="current-password"
              />
            </div>

            <div className="reveal-3">
              <PasswordField
                id={newId}
                label="New password"
                registration={register}
                fieldName="newPassword"
                error={errors.newPassword?.message}
              />
            </div>

            <div className="reveal-3">
              <PasswordField
                id={confirmId}
                label="Confirm new password"
                registration={register}
                fieldName="confirmPassword"
                error={errors.confirmPassword?.message}
              />
            </div>

            {/* Server error — §2.21 alert row, system-error token */}
            {serverError && (
              <div
                role="alert"
                style={{
                  padding: '11px 14px',
                  borderRadius: 'var(--radius-md)',
                  borderLeft: '3px solid var(--error)',
                  background: 'color-mix(in srgb, var(--error) 10%, transparent)',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--error)',
                    fontWeight: 500,
                  }}
                >
                  {serverError}
                </p>
              </div>
            )}

            {/* Submit — the screen's single lime pill CTA */}
            <button
              type="submit"
              disabled={isPending || successMessage}
              className="reveal-4 focus-ring"
              style={{
                height: 44,
                width: '100%',
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
                padding: '0 20px',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                letterSpacing: '0.01em',
                color: 'var(--btn-primary-fg)',
                background: 'var(--btn-primary-bg)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                cursor: isPending || successMessage ? 'not-allowed' : 'pointer',
                transition: 'background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out), opacity var(--dur-fast) var(--ease-out)',
                opacity: isPending || successMessage ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isPending && !successMessage) {
                  (e.currentTarget as HTMLButtonElement).style.background = CTA_HOVER_BG;
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = 'var(--btn-primary-bg)';
                el.style.transform = 'none';
              }}
              onMouseDown={(e) => {
                if (!isPending && !successMessage) {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)';
                }
              }}
              onMouseUp={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'none';
              }}
            >
              {isPending ? (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                    style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}
                  >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Updating…
                </>
              ) : (
                'Update password'
              )}
            </button>

            {/* Skip link — only for voluntary change (not force reset) */}
            {!mustChangePassword && (
              <button
                type="button"
                className="reveal-5 focus-ring"
                onClick={() => navigate('/', { replace: true })}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-muted)',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  padding: 0,
                  alignSelf: 'center',
                  transition: 'color var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
                }}
              >
                Cancel
              </button>
            )}
          </form>

          {/* Enterprise footer */}
          <div className="reveal-5" style={{ marginTop: 28 }}>
            <div style={{ height: 1, background: 'var(--border)', marginBottom: 14 }} />
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-2xs)',
                letterSpacing: '0.12em',
                // No opacity multiplier: --text-subtle alone clears the 3:1
                // secondary floor in both themes (§4.1 #10); 0.85× pushed the
                // light theme to ~2.9:1.
                color: 'var(--text-subtle)',
              }}
            >
              SEEDZ MANAGE · v1.0
            </p>
          </div>
        </div>
      </div>

      {/* Spinner keyframe + auth-scoped token fixes */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        /* §2.16 placeholder --text-low role: token colour (≥4.5:1 on the well
           in BOTH themes) instead of the untokenized preflight gray.
           opacity:1 cancels Firefox's default placeholder dimming. Scoped to
           this surface — the app-global rule belongs in index.css. */
        .auth-screen input::placeholder { color: var(--text-muted); opacity: 1; }
        /* §1.7 motion band: entrance reveals run at --dur (200ms base), not
           --dur-slow (340ms). Reduced-motion (!important in index.css) still
           wins over this override. */
        .auth-screen .reveal-1, .auth-screen .reveal-2, .auth-screen .reveal-3,
        .auth-screen .reveal-4, .auth-screen .reveal-5 {
          animation-duration: var(--dur);
        }
      `}</style>
    </div>
  );
}
